import {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {CookiesKey, useCookies} from "@/app/utils/cookies";
import {route} from "@/app/routes/AppRoutes";
import {
  NewUserModel,
  UpdateUserLocationModel,
  UserChangeGeoSettingsModel,
  UserRequests
} from "@/app/api/user";

interface ScreenState {
  hasError?: string;
  hasLoading: boolean;
  unavailable: boolean
  radius: number
  user?: NewUserModel
}

const useHome = () => {
  const navigate = useNavigate();
  const {deleteCookie} = useCookies();
  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    hasLoading: true,
    unavailable: false,
    radius: 0.8,
    user: undefined
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const init = useCallback(async () => {
    try {
      const user = await UserRequests.getCurrentUser();
      // If user profile is not yet created (profile === null or id is null), redirect to onboarding
      const anyUser = user as any;
      if (user.id == null || anyUser.profile === null) {
        navigate(route.onboarding);
        return;
      }
      updateState({
        user,
        hasLoading: false,
        unavailable: user.user?.available ?? false,
        radius: user.user?.preferredRadiusKm ?? 0.8
      });

    } catch (e) {
      console.error(e);
    }
  }, [updateState, navigate]);

  useEffect(() => {
    init();
  }, [init]);

  const logout = useCallback(() => {
    deleteCookie(CookiesKey.token);
    navigate(route.signup)
  }, [deleteCookie, navigate]);

  const goToOnboarding = useCallback(() => {
    navigate(route.onboarding);
  }, [navigate]);

  const changeUnavailable = useCallback(async (unavailable: boolean) => {
    const data = {available: unavailable} as UserChangeGeoSettingsModel;
    await UserRequests.changeGeoSetting(data)
    updateState({unavailable});
  }, [updateState]);

  const setRadius = useCallback(async (radius: number) => {
    const data = {preferredRadiusKm: radius} as UserChangeGeoSettingsModel;
    await UserRequests.changeGeoSetting(data)
    updateState({radius});
  }, [updateState]);

  const goToFindPlayers = useCallback(() => {
    navigate(route.findPlayers);
  }, [navigate]);

  const updateLocationOnSever = useCallback(async (position?: UpdateUserLocationModel) => {
    if (position === undefined) return;
    try {
      UserRequests.changeUserLocation(position).then();
    } catch (e) {
      console.error(e);
    }
  }, [updateState]);

  return {
    state,
    logout,
    goToOnboarding,
    changeUnavailable,
    setRadius,
    goToFindPlayers,
    updateLocationOnSever
  };
};

export default useHome;